import { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';

import {
  generatePrecisionMeetingBrief,
  getPrecisionMeetingBrief,
} from '../../../services/precisionMeetingApi';
import { BriefViewer, PrecisionMeetingBrief } from '../../Brief';
import { Button } from '../../Shared';

const OPENING_MESSAGE = 'Opening the meeting brief…';
const GENERATING_MESSAGE =
  'Reading the call transcript and preparing the meeting brief. Usually 20 to 40 seconds.';

const getSubtitle = (contactName, brief) => {
  if (!brief) return contactName;

  const { meeting_metadata: metadata, confidence_and_sources: confidence } =
    brief;

  return [
    metadata.prospect_name,
    confidence.call_date && `call on ${confidence.call_date}`,
    `meeting ${[
      metadata.meeting_date,
      `${metadata.meeting_duration_minutes} min`,
    ]
      .filter(Boolean)
      .join(', ')}`,
  ]
    .filter(Boolean)
    .join(' · ');
};

// target: { callRecordingId } from Playback or { apptId } from the Bookings Report.
export const PrecisionMeetingModal = ({ target, contactName, onClose }) => {
  const abortControllerRef = useRef(null);
  const hasBriefRef = useRef(false);

  const [stored, setStored] = useState(null);
  const [loadingMessage, setLoadingMessage] = useState(OPENING_MESSAGE);
  const [errorMessage, setErrorMessage] = useState('');

  const getSignal = () => {
    abortControllerRef.current?.abort();
    abortControllerRef.current = new AbortController();
    return abortControllerRef.current.signal;
  };

  const generate = useCallback(
    async signal => {
      setLoadingMessage(GENERATING_MESSAGE);
      setErrorMessage('');

      try {
        setStored(await generatePrecisionMeetingBrief(target, signal));
        setLoadingMessage('');
      } catch (e) {
        if (e?.name === 'AbortError') return;

        setLoadingMessage('');
        // A brief that is already shown stays: the failed attempt doesn't take it away.
        if (hasBriefRef.current) {
          toast.error(e.message, { position: 'top-right', autoClose: 3000 });
        } else {
          setErrorMessage(e.message);
        }
      }
    },
    [target],
  );

  const open = useCallback(
    async signal => {
      setLoadingMessage(OPENING_MESSAGE);
      setErrorMessage('');

      try {
        const data = await getPrecisionMeetingBrief(target, signal);
        if (data.brief) {
          setStored(data);
          setLoadingMessage('');
          return;
        }
        await generate(signal);
      } catch (e) {
        if (e?.name === 'AbortError') return;

        setLoadingMessage('');
        setErrorMessage(e.message);
      }
    },
    [target, generate],
  );

  useEffect(() => {
    open(getSignal());

    return () => abortControllerRef.current?.abort();
    // The server keeps generating after closing and stores the brief, so it opens right away next time.
  }, [open]);

  const brief = stored?.brief;
  hasBriefRef.current = !!brief;

  return (
    <BriefViewer
      title="Precision Meeting brief"
      subtitle={getSubtitle(contactName, brief)}
      notice={
        !loadingMessage &&
        stored?.promptChanged &&
        "This client's prompt has changed since the brief was prepared. Regenerate it to use the new prompt."
      }
      loadingMessage={loadingMessage}
      errorMessage={errorMessage}
      onRetry={() => open(getSignal())}
      isPrintDisabled={!brief || !!loadingMessage}
      onClose={onClose}
      actions={
        <Button
          type="button"
          title="Regenerate"
          className="h-10 py-2 px-4 text-[14px]/[20px]"
          disabled={!brief || !!loadingMessage}
          onClick={() => generate(getSignal())}
        />
      }
    >
      {brief && <PrecisionMeetingBrief brief={brief} />}
    </BriefViewer>
  );
};
